<template>
    <div id="BlogComponent">
        <NavBar />

        <main>
        <section class="blog-hero">
            <b-container>
                <div class="hero-content">
                    <p class="hero-kicker">CHILL BYTE / CONOCIMIENTO</p>
                    <h1 class="hero-title">Blog de Estrategia Empresarial y Transformación Digital</h1>
                    <p class="hero-subtitle">
                        Análisis prácticos sobre NetSuite, SAT, facturación electrónica y eficiencia operativa para equipos directivos.
                    </p>
                    <div class="hero-stats">
                        <span>{{ posts.length }} artículos</span>
                        <span>{{ categories.length }} categorías</span>
                        <span>NetSuite · SAT · Operación</span>
                    </div>
                </div>
            </b-container>
        </section>

        <b-container class="blog-content">
            <b-row>
                <b-col lg="8" md="12" class="mb-4">
                    <div class="listing-heading">
                        <div><p class="section-label">EXPLORA EL BLOG</p><h2>Artículos y guías</h2></div>
                        <span>{{ filteredPosts.length }} resultados</span>
                    </div>
                    <div class="posts-grid">
                        <article v-for="(post) in filteredPosts" :key="post.slug" class="post-card">
                            <div class="post-image-wrapper">
                                <img
                                    :src="require(`@/assets/img/blog/${post.image}.webp`)"
                                    :alt="post.altImage || post.title"
                                    class="post-image"
                                    loading="lazy"
                                />
                            </div>
                            <div class="post-content">
                                <h3 class="post-title">{{ post.title }}</h3>
                                <p class="post-meta">
                                    <time :datetime="post.date">{{ formatDate(post.date) }}</time>
                                    <span>{{ estimateReadTime(post) }} min</span>
                                </p>
                                <p class="post-excerpt">{{ post.excerpt }}</p>
                                <ul class="post-tags">
                                    <li v-for="category in post.categories" :key="`${post.slug}-${category}`">{{ category }}</li>
                                </ul>
                                <router-link class="btn-read-more" :to="postRoute(post.slug)">
                                    Ver artículo completo
                                    <b-icon icon="arrow-up-right" aria-hidden="true" />
                                </router-link>
                            </div>
                        </article>

                        <p v-if="!filteredPosts.length" class="empty-posts">
                            No encontramos artículos con este filtro. Prueba otra categoría o cambia la búsqueda.
                        </p>
                    </div>
                </b-col>

                <b-col lg="4" md="12">
                    <aside class="sidebar">
                        <div class="sidebar-card">
                            <h2 class="sidebar-title">Buscar artículos</h2>
                            <input
                                v-model="searchQuery"
                                class="search-input"
                                type="text"
                                placeholder="Ej. CFDI, NetSuite, pymes"
                                aria-label="Buscar articulos"
                            />
                        </div>

                        <div class="sidebar-card">
                            <h2 class="sidebar-title">Categorías</h2>
                            <div class="category-list">
                                <button
                                    type="button"
                                    class="category-item"
                                    :class="{ active: selectedCategory === '' }"
                                    @click="filterByCategory('')"
                                >
                                    Todas
                                </button>
                                <button
                                    v-for="category in categories"
                                    :key="category"
                                    type="button"
                                    class="category-item"
                                    :class="{ active: selectedCategory === category }"
                                    @click="filterByCategory(category)"
                                >
                                    {{ category }}
                                </button>
                            </div>
                        </div>

                        <div class="sidebar-card">
                            <h2 class="sidebar-title">Índice del blog</h2>
                            <ol class="blog-index-list">
                                <li v-for="post in filteredPosts" :key="`index-${post.slug}`">
                                    <router-link :to="postRoute(post.slug)">{{ post.title }}</router-link>
                                </li>
                            </ol>
                        </div>

                        <div class="sidebar-card">
                            <h2 class="sidebar-title">Posts recientes</h2>
                            <div class="recent-posts-list">
                                <router-link
                                    v-for="recentPost in recentPosts"
                                    :key="recentPost.slug"
                                    :to="postRoute(recentPost.slug)"
                                    class="recent-post-item"
                                >
                                    <span class="recent-post-title">{{ recentPost.title }}</span>
                                    <time class="recent-post-date" :datetime="recentPost.date">{{ formatDate(recentPost.date) }}</time>
                                </router-link>
                            </div>
                        </div>
                    </aside>
                </b-col>
            </b-row>
        </b-container>
        </main>

        <ContactForm />
        <FooterChillByte />
    </div>
</template>

<script>
import NavBar from "../components/Template/NavBar.vue";
import Footer from "../components/Template/Footer.vue";
import ContactForm from "@/components/Template/ContactForm.vue";
import { BLOG_POSTS_BY_DATE, SITE_URL } from "@/data/blogPosts";

export default {
    name: "BlogView",
    components: {
        NavBar,
        FooterChillByte: Footer,
        ContactForm,
    },
    data() {
        return {
            posts: BLOG_POSTS_BY_DATE,
            selectedCategory: "",
            searchQuery: typeof this.$route.query.q === "string" ? this.$route.query.q : "",
        };
    },
    computed: {
        categories() {
            const allCategories = this.posts.flatMap((post) => post.categories || []);
            return [...new Set(allCategories)];
        },
        filteredPosts() {
            const query = this.searchQuery.trim().toLowerCase();

            return this.posts.filter((post) => {
                const byCategory = this.selectedCategory
                    ? (post.categories || []).includes(this.selectedCategory)
                    : true;

                const searchable = [
                    post.title,
                    post.excerpt,
                    post.summary,
                    ...(post.categories || []),
                    ...(post.keywords || []),
                ]
                    .join(" ")
                    .toLowerCase();

                const bySearch = query ? searchable.includes(query) : true;

                return byCategory && bySearch;
            });
        },
        recentPosts() {
            return this.posts.slice(0, 4);
        },
    },
    watch: {
        "$route.query.q"(query) {
            this.searchQuery = typeof query === "string" ? query : "";
        },
    },
    metaInfo() {
        const title = "Blog NetSuite para Empresas que Ya Usan NetSuite | Chill Byte";
        const description = "Guias para empresas con NetSuite activo: optimizacion post implementacion, adopcion, reportes, integraciones y cumplimiento SAT.";
        const canonicalUrl = `${SITE_URL}/blog`;
        const blogListSchema = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: this.posts.map((post, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `${SITE_URL}/blog/${post.slug}`,
                name: post.title,
            })),
        };
        const websiteSchema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Chill Byte Solutions",
            url: SITE_URL,
            potentialAction: {
                "@type": "SearchAction",
                target: `${SITE_URL}/blog?q={search_term_string}`,
                "query-input": "required name=search_term_string",
            },
        };

        return {
            title,
            link: [{ rel: "canonical", href: canonicalUrl }],
            meta: [
                { vmid: "description", name: "description", content: description },
                { vmid: "robots", name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" },
                { vmid: "og:type", property: "og:type", content: "website" },
                { vmid: "og:locale", property: "og:locale", content: "es_MX" },
                { vmid: "og:title", property: "og:title", content: title },
                { vmid: "og:description", property: "og:description", content: description },
                { vmid: "og:url", property: "og:url", content: canonicalUrl },
                { vmid: "og:image", property: "og:image", content: `${SITE_URL}/img/blog/sat_blog1.webp` },
                { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
                { vmid: "twitter:title", name: "twitter:title", content: title },
                { vmid: "twitter:description", name: "twitter:description", content: description },
                { vmid: "twitter:image", name: "twitter:image", content: `${SITE_URL}/img/blog/sat_blog1.webp` },
            ],
            script: [
                { vmid: "blog-list-schema", type: "application/ld+json", innerHTML: JSON.stringify(blogListSchema) },
                { vmid: "website-schema", type: "application/ld+json", innerHTML: JSON.stringify(websiteSchema) },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                "blog-list-schema": ["innerHTML"],
                "website-schema": ["innerHTML"],
            },
        };
    },
    methods: {
        postRoute(slug) {
            return { name: "post", params: { slug } };
        },
        filterByCategory(category) {
            this.selectedCategory = category;
        },
        formatDate(date) {
            try {
                return new Intl.DateTimeFormat("es-MX", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }).format(new Date(date));
            } catch (error) {
                return date;
            }
        },
        estimateReadTime(post) {
            const source = [post.title, post.summary, post.excerpt, ...(post.keywords || [])].join(" ");
            const words = source.trim().split(/\s+/).filter(Boolean).length;
            return Math.max(1, Math.ceil(words / 200));
        },
    },
};
</script>

<style scoped>
#BlogComponent {
    --ink: #101828;
    --muted: #475467;
    --line: #dbe3ee;
    --accent: #146ef5;
    color: var(--ink);
    letter-spacing: 0;
    background: #ffffff;
    min-height: 100vh;
}

.blog-hero {
    background: #f7f9fc;
    border-bottom: 1px solid var(--line);
    padding: 88px 0 76px;
    position: relative;
    overflow: hidden;
}

.blog-hero::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(20, 110, 245, .05) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 110, 245, .05) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: linear-gradient(to right, #000, transparent 85%);
    pointer-events: none;
}

.hero-content {
    position: relative;
    z-index: 1;
    max-width: 820px;
}

.hero-kicker {
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: .12em;
    font-size: .76rem;
    font-weight: 800;
    color: #075fcf;
}

.hero-title {
    font-size: clamp(2.4rem, 4.5vw, 4.2rem) !important;
    line-height: 1.08;
    font-weight: 800;
    color: var(--ink) !important;
    margin: 0 0 22px !important;
}

.hero-subtitle {
    font-size: 1.18rem;
    line-height: 1.65;
    color: var(--muted);
    margin-bottom: 30px;
    max-width: 760px;
}

.hero-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
}

.hero-stats span {
    padding: 4px 18px;
    border-left: 1px solid #cbd5e1;
    color: var(--muted);
    font-size: .83rem;
    font-weight: 700;
}

.hero-stats span:first-child {
    padding-left: 0;
    border: 0;
}

.blog-content {
    max-width: 1240px;
    padding: 64px 15px 100px;
}

.listing-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 26px;
}

.section-label {
    margin-bottom: 10px;
    color: #075fcf;
    font-size: .75rem;
    font-weight: 800;
    letter-spacing: .12em;
}

.listing-heading h2 {
    margin: 0 !important;
    color: var(--ink);
    font-size: clamp(1.7rem, 3vw, 2.4rem);
    font-weight: 800;
}

.listing-heading > span {
    white-space: nowrap;
    color: var(--muted);
    font-size: .86rem;
}

.posts-grid {
    display: grid;
    gap: 0;
    border-top: 1px solid var(--line);
}

.post-card {
    display: grid;
    grid-template-columns: minmax(180px, 34%) minmax(0, 1fr);
    gap: 26px;
    padding: 28px 0;
    border-bottom: 1px solid var(--line);
}

.post-card:hover {
    background: #f8faff;
}

.post-image-wrapper {
    aspect-ratio: 4 / 3;
    align-self: start;
    overflow: hidden;
    border-radius: 6px;
    background: #eaf0f7;
}

.post-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.post-content {
    padding: 2px 0;
    display: flex;
    flex-direction: column;
}

.post-index {
    margin: 0 0 8px;
    color: #0f6f8f;
    font-weight: 700;
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.post-title {
    margin: 0;
    color: var(--ink);
    font-size: clamp(1.2rem, 2vw, 1.6rem);
    font-weight: 800;
    line-height: 1.25;
}

.post-meta {
    margin: 10px 0;
    display: flex;
    gap: 12px;
    font-size: 0.84rem;
    color: var(--muted);
}

.post-excerpt {
    margin: 0 0 12px;
    line-height: 1.7;
    color: var(--muted);
}

.post-tags {
    margin: 0 0 16px;
    padding: 0;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.post-tags li {
    padding: 4px 0;
    color: #075fcf;
    font-size: 0.8rem;
    font-weight: 800;
}

.post-tags li + li::before {
    content: "/";
    color: #98a2b3;
    margin-right: 8px;
}

.btn-read-more {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    color: #075fcf;
    padding: 4px 0;
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-read-more:hover {
    color: #074da6;
    text-decoration: underline;
}

.empty-posts {
    margin: 0;
    padding: 18px;
    border-radius: 10px;
    background: #ffffff;
    color: #344a5a;
    border: 1px solid #dce8f1;
}

.sidebar {
    position: sticky;
    top: 24px;
}

.sidebar-card {
    border-top: 1px solid var(--line);
    padding: 22px 0;
}

.sidebar-title {
    margin: 0 0 12px;
    color: var(--ink);
    font-size: 1rem;
    font-weight: 800;
}

.search-input {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 12px 14px;
    color: var(--ink);
    outline: none;
}

.search-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(20, 110, 245, .14);
}

.category-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.category-item {
    border: 1px solid var(--line);
    background: #fff;
    color: var(--muted);
    border-radius: 6px;
    padding: 8px 11px;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
}

.category-item.active,
.category-item:hover {
    background: #edf4ff;
    border-color: #9fc3fb;
    color: #075fcf;
}

.blog-index-list {
    margin: 0;
    padding-left: 1.2rem;
}

.blog-index-list li {
    margin-bottom: 8px;
}

.blog-index-list a {
    color: var(--muted);
    text-decoration: none;
    line-height: 1.5;
}

.blog-index-list a:hover {
    color: #075fcf;
    text-decoration: underline;
}

.recent-posts-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.recent-post-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid var(--line);
    padding: 10px 0;
    text-decoration: none;
}

.recent-post-title {
    font-size: 0.9rem;
    color: var(--ink);
    font-weight: 600;
}

.recent-post-date {
    font-size: 0.8rem;
    color: #5b7081;
}

@media (max-width: 1100px) {
    .post-card {
        grid-template-columns: minmax(160px, 32%) minmax(0, 1fr);
    }
}

@media (max-width: 991px) {
    .sidebar {
        position: static;
        margin-top: 8px;
    }
}

@media (max-width: 768px) {
    .blog-hero {
        padding: 64px 0;
    }

    .blog-content {
        padding-top: 44px;
    }

    .hero-subtitle {
        font-size: 0.98rem;
    }

    .post-card {
        grid-template-columns: 1fr;
    }

    .post-image-wrapper {
        aspect-ratio: 16 / 9;
    }

    .post-content {
        padding: 0;
    }
}

@media (max-width: 575px) {
    .hero-stats {
        row-gap: 8px;
    }

    .hero-stats span {
        font-size: 0.78rem;
    }

    .sidebar-card {
        padding: 22px 0;
    }

    .listing-heading {
        align-items: start;
        flex-direction: column;
        gap: 6px;
    }
}
</style>
